import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { MessageSquare, Search, Download, Calendar, Mail, Phone, User, Eye, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/components/ui/use-toast';

interface ContactSubmission {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
}

export default function AdminFormsContact() {
  const { isAdmin, isLoading: authLoading, user, adminUser } = useAdminAuth();
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [retryCount, setRetryCount] = useState(0);
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('new');
  const [savingStatus, setSavingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const canDelete = adminUser?.role === 'admin';

  useEffect(() => {
    if (!authLoading && isAdmin && user) {
      fetchSubmissions();
    }
  }, [authLoading, isAdmin, user]);

  // Support drill-down via query params like ?status=new&q=email
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const status = params.get('status');
      const q = params.get('q');
      if (status) setStatusFilter(status);
      if (q) setSearchTerm(q);
    } catch {}
  }, []);

  const fetchSubmissions = async () => {
    if (!isAdmin || !user) {
      console.log('Not authenticated as admin, skipping fetch');
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching contact submissions via dedicated RPC');
      const { data, error } = await supabase.rpc('list_contact_submissions');

      if (error) {
        console.error('Supabase RPC error:', error);
        throw error;
      }

      const result = data as any;
      let rows: any[] = [];
      if (Array.isArray(result)) rows = result;
      else if (typeof result === 'string') {
        try { rows = JSON.parse(result); } catch (e) { rows = []; }
      }

      console.log('Successfully fetched submissions:', rows.length);
      setSubmissions((rows || []) as ContactSubmission[]);
      setRetryCount(0);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };
  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch = 
      submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      'new': 'destructive',
      'in_progress': 'default',
      'resolved': 'default'
    } as const;
    
    return <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>{status}</Badge>;
  };

  const handleViewSubmission = (submission: ContactSubmission) => {
    setSelectedSubmission(submission);
    setSelectedStatus(submission.status);
    setIsViewModalOpen(true);
  };

  const handleStatusSave = async () => {
    if (!selectedSubmission) {
      toast({
        variant: 'destructive',
        title: 'No submission selected',
        description: 'Please select a submission first.',
      });
      return;
    }

    try {
      setSavingStatus(true);
      const { error } = await supabase
        .from('contact_submissions')
        .update({ status: selectedStatus, updated_at: new Date().toISOString() })
        .eq('id', selectedSubmission.id);

      if (error) throw error;

      setSubmissions(prev => prev.map(s => s.id === selectedSubmission.id ? { ...s, status: selectedStatus } : s));
      setSelectedSubmission({ ...selectedSubmission, status: selectedStatus });
      toast({ title: 'Status updated', description: `Marked as ${selectedStatus}.` });
    } catch (err: any) {
      console.error('Failed to update status', err);
      toast({ variant: 'destructive', title: 'Update failed', description: err.message || 'Could not update status.' });
    } finally {
      setSavingStatus(false);
    }
  };

  // Handle individual delete
  const handleDeleteSingle = async (id: string) => {
    try {
      setDeleting(true);
      const { data, error } = await supabase
        .functions
        .invoke('admin-delete-contact-submissions', { body: { ids: [id] } });

      if (error) throw error;

      await fetchSubmissions();
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      toast({ title: 'Submission deleted', description: 'The contact submission was removed.' });
    } catch (err: any) {
      console.error('Failed to delete submission', err);
      toast({ variant: 'destructive', title: 'Delete failed', description: err.message || 'Failed to delete submission.' });
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      setDeleting(true);
      const { data, error } = await supabase
        .functions
        .invoke('admin-delete-contact-submissions', { body: { ids: Array.from(selectedIds) } });

      if (error) throw error;

      await fetchSubmissions();
      setSelectedIds(new Set());
      toast({ 
        title: 'Submissions deleted', 
        description: `Successfully deleted ${selectedIds.size} submissions.` 
      });
    } catch (err: any) {
      console.error('Failed to bulk delete submissions', err);
      toast({ 
        variant: 'destructive', 
        title: 'Bulk delete failed', 
        description: err.message || 'Failed to delete selected submissions.' 
      });
    } finally {
      setDeleting(false);
      setShowBulkDeleteDialog(false);
    }
  };

  // Handle modal delete (existing functionality)
  const handleDelete = async () => {
    if (!selectedSubmission) return;
    try {
      setDeleting(true);
      const { data, error } = await supabase
        .functions
        .invoke('admin-delete-contact-submissions', { body: { ids: [selectedSubmission.id] } });

      if (error) throw error;

      await fetchSubmissions();
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(selectedSubmission.id);
        return newSet;
      });
      setIsViewModalOpen(false);
      toast({ title: 'Submission deleted', description: 'The contact submission was removed.' });
    } catch (err: any) {
      console.error('Failed to delete submission', err);
      toast({ variant: 'destructive', title: 'Delete failed', description: err.message || 'Failed to delete submission.' });
    } finally {
      setDeleting(false);
    }
  };

  // Checkbox handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredSubmissions.map(s => s.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected = filteredSubmissions.length > 0 && filteredSubmissions.every(s => selectedIds.has(s.id));
  const isIndeterminate = selectedIds.size > 0 && !isAllSelected;

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contact Submissions</h1>
          <p className="text-muted-foreground">
            Manage and respond to contact form submissions
          </p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && canDelete && (
            <Button 
              variant="destructive" 
              onClick={() => setShowBulkDeleteDialog(true)}
              disabled={deleting}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Selected ({selectedIds.size})
            </Button>
          )}
          <Button>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Submissions</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{submissions.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {submissions.filter(s => new Date(s.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New</CardTitle>
            <Badge variant="destructive" className="h-4 w-4 p-0"></Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {submissions.filter(s => s.status === 'new').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Response Rate</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {submissions.length > 0 ? Math.round((submissions.filter(s => s.status !== 'new').length / submissions.length) * 100) : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Submissions</CardTitle>
          <CardDescription>Search and filter contact form submissions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or subject..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Submissions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Submissions ({filteredSubmissions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    className={isIndeterminate ? "data-[state=indeterminate]:bg-primary" : ""}
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubmissions.map((submission) => (
                <TableRow key={submission.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(submission.id)}
                      onCheckedChange={(checked) => handleSelectOne(submission.id, !!checked)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      {submission.first_name} {submission.last_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center text-sm">
                        <Mail className="mr-1 h-3 w-3" />
                        {submission.email}
                      </div>
                      {submission.phone && (
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Phone className="mr-1 h-3 w-3" />
                          {submission.phone}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{submission.subject}</TableCell>
                  <TableCell>
                    <div className="max-w-xs truncate" title={submission.message}>
                      {submission.message}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(submission.status)}</TableCell>
                  <TableCell>
                    {format(new Date(submission.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewSubmission(submission)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setItemToDelete(submission.id);
                            setShowDeleteDialog(true);
                          }}
                          disabled={deleting}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredSubmissions.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No submissions found matching your criteria
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Submission Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contact Submission Details</DialogTitle>
            <DialogDescription>
              Complete information for this contact submission
            </DialogDescription>
          </DialogHeader>
          
          {selectedSubmission && (
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="grid gap-4">
                <h3 className="text-lg font-semibold border-b pb-2">Contact Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">First Name</label>
                    <p className="font-medium">{selectedSubmission.first_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Name</label>
                    <p className="font-medium">{selectedSubmission.last_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="font-medium">{selectedSubmission.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone</label>
                    <p className="font-medium">{selectedSubmission.phone || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Message Details */}
              <div className="grid gap-4">
                <h3 className="text-lg font-semibold border-b pb-2">Message Details</h3>
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Subject</label>
                    <p className="font-medium">{selectedSubmission.subject}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Message</label>
                    <div className="bg-muted p-4 rounded-md">
                      <p className="whitespace-pre-wrap">{selectedSubmission.message}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Administrative Information */}
              <div className="grid gap-4">
                <h3 className="text-lg font-semibold border-b pb-2">Administrative Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={handleStatusSave} disabled={savingStatus}>
                        {savingStatus ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Submitted On</label>
                    <p className="font-medium">{format(new Date(selectedSubmission.created_at), 'PPP pp')}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
                  Close
                </Button>
                {canDelete && (
                  <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    {deleting ? 'Deleting...' : 'Delete'}
                  </Button>
                )}
                <Button onClick={() => window.open(`mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject}`)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Reply
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Individual Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the contact submission.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => itemToDelete && handleDeleteSingle(itemToDelete)}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multiple Submissions</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} contact submissions? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : `Delete ${selectedIds.size} Items`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}